/**
 * Speaking the answer aloud.
 *
 * The prompt is never spoken — the point is to hear the word being learned, in
 * the language being learned. Each deck names the answer language (BCP-47):
 * IELTS is marked against British English, so "en-GB" is asked for; the
 * Korean deck asks for "ko-KR". A voice for the exact tag is preferred, then
 * any voice of that language.
 *
 * Everything here degrades to silence rather than throwing: speech synthesis is
 * missing, muted or blocked often enough (older browsers, locked-down devices,
 * iOS before a user gesture) that it cannot be treated as reliable.
 */

const DEFAULT_LANG = "en-GB";

/** Chosen voice per requested language tag; refreshed when voices load. */
const chosen = new Map<string, SpeechSynthesisVoice | null>();

function synth(): SpeechSynthesis | null {
  return typeof window !== "undefined" && "speechSynthesis" in window
    ? window.speechSynthesis
    : null;
}

function pickVoice(lang: string): SpeechSynthesisVoice | null {
  const s = synth();
  if (!s) return null;
  const voices = s.getVoices();
  if (voices.length === 0) return null;
  const base = lang.split("-")[0]!;
  const tag = (v: SpeechSynthesisVoice) => v.lang.replace("_", "-");
  const voice =
    voices.find((v) => tag(v) === lang) ??
    voices.find((v) => tag(v).startsWith(`${base}-`)) ??
    voices.find((v) => tag(v).startsWith(base)) ??
    null;
  chosen.set(lang, voice);
  return voice;
}

// Voices load asynchronously, and on some browsers the first call returns an
// empty list. Forget what was picked when the list arrives, so the next
// utterance picks again from the full list.
const s = synth();
if (s) {
  s.addEventListener?.("voiceschanged", () => chosen.clear());
}

export function speechSupported(): boolean {
  return synth() !== null;
}

/**
 * Speak `text`, replacing anything already being said. Safe to call from a
 * keyboard or click handler, which is what iOS requires for the first
 * utterance — never from a timer.
 */
export function speak(text: string, lang: string = DEFAULT_LANG): void {
  const s = synth();
  if (!s || !text.trim()) return;
  try {
    s.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = chosen.has(lang) ? chosen.get(lang)! : pickVoice(lang);
    if (voice) utterance.voice = voice;
    // The tag is set even without a matching voice: the engine may still
    // pick a sensible default for the language.
    utterance.lang = voice?.lang ?? lang;
    // A shade under natural pace: these are unfamiliar words.
    utterance.rate = 0.95;
    s.speak(utterance);
  } catch {
    // Unsupported, blocked, or the device refused. Silence is an acceptable
    // outcome for a pronunciation aid.
  }
}

export function stopSpeaking(): void {
  try {
    synth()?.cancel();
  } catch {
    // nothing to stop
  }
}
